import {
  Geraet,
  Incident,
  IncidentError,
  IncidentsApi,
  LogLevel,
  UserAgent,
} from '@now/clientloggingservice-api/lib/api';
import { Configuration } from '@now/clientloggingservice-api/lib/configuration';
import { v4 as uuidv4 } from 'uuid';
import { ConfigProvider } from '../config/config-provider';
import { CookieUtil } from '../utils/cookie-util';
import { CorrelationId } from '../models/correlation-id';
import { formatCorrelationId } from '../utils/format-correlation-id';
import { BaJwt, TokenEnsurer } from '@websso/oiam-oauth-types';
import UAParser from 'ua-parser-js';
import { isAxiosError } from 'axios';

export const createCorrelationIdForDiagnosticId = (clientApiKey: string, diagnosticId: string) => {
  // Hinweis: die Verwendung der Methode in NowSession führt zu einem Zyklus und zu Testfehlern
  const sessionId = CookieUtil.getCookieValue('now-session-id');
  return {
    clientApiKey: clientApiKey,
    sessionId: sessionId ?? uuidv4(),
    requestId: diagnosticId,
  } satisfies CorrelationId;
};

export const createHeaders = (
  clientApiKey: string,
  diagnosticId: string,
  correlationId?: CorrelationId,
  jwt?: string,
) => {
  const correlationIdStr = formatCorrelationId(
    correlationId ?? createCorrelationIdForDiagnosticId(clientApiKey, diagnosticId),
  );

  if (jwt) {
    return {
      headers: {
        Authorization: 'Bearer ' + jwt,
        'x-api-key': clientApiKey,
        BACorrelationID: correlationIdStr,
      },
      withCredentials: true,
    };
  } else {
    return {
      headers: {
        'x-api-key': clientApiKey,
        BACorrelationID: correlationIdStr,
      },
    };
  }
};

export type IncidentType =
  | 'FEEDBACK_MAIL_FAILED'
  | 'MATOMO_FAILED_SCRIPT_URL'
  | 'PROFILE_FETCHER_FAILED'
  | 'DKZ_FETCHER_FAILED'
  | 'GET_FAVORITEN_FAILED'
  | 'CREATE_FAVORITEN_FAILED'
  | 'DELETE_FAVORITEN_FAILED'
  | 'MATOMO_FAILED_WITH_ERROR'
  | 'DKZ_FETCHER_FAILED_WITH_ERROR'
  | 'READ_FEATURE_TOGGLE_CONFIG_FAILED'
  | 'CREATE_FAVORITEN_ATTRIBUTES_INVALID_CHARACTERS_REMOVED'
  | 'GET_BENUTZERDATEN_FAILED'
  | 'GET_MITTEILUNGEN_FAILED';

type Log = {
  code: string;
  message: string;
  logLevel?: LogLevel;
};

const possibleLogs: Record<IncidentType, Log> = {
  FEEDBACK_MAIL_FAILED: {
    code: 'DPLC-0001',
    message: 'Feedback-Mail konnte nicht gesendet werden',
  },
  MATOMO_FAILED_SCRIPT_URL: {
    code: 'DPLC-0002',
    message: 'Matomo konnte nicht geladen werden. Script URL nicht ersetzt',
  },
  PROFILE_FETCHER_FAILED: {
    code: 'DPLC-0003',
    message: 'Abruf der Session-ID fehlgeschlagen',
    logLevel: 'WARN',
  },
  DKZ_FETCHER_FAILED: {
    code: 'DPLC-0004',
    message: 'DKZ-Aufruf fehlgeschlagen',
  },
  GET_FAVORITEN_FAILED: {
    code: 'DPLC-0005',
    message: 'Favoriten-Abruf des Nutzers nicht möglich',
  },
  CREATE_FAVORITEN_FAILED: {
    code: 'DPLC-0006',
    message: 'Erstellen eines Favoriten nicht möglich',
  },
  DELETE_FAVORITEN_FAILED: {
    code: 'DPLC-0007',
    message: 'Löschen eines Favoriten nicht möglich',
  },
  MATOMO_FAILED_WITH_ERROR: {
    code: 'DPLC-0008',
    message: 'Matomo konnte nicht geladen werden. Es trat ein Fehler auf',
    logLevel: 'WARN',
  },
  DKZ_FETCHER_FAILED_WITH_ERROR: {
    code: 'DPLC-0009',
    message: 'DKZ-Aufruf fehlgeschlagen mit Error',
  },
  READ_FEATURE_TOGGLE_CONFIG_FAILED: {
    code: 'DPLC-0010',
    message: 'Abruf der Feature-Toggle-Konfiguration fehlgeschlagen',
  },
  CREATE_FAVORITEN_ATTRIBUTES_INVALID_CHARACTERS_REMOVED: {
    code: 'DPLC-0011',
    message: 'Ungültige Zeichen wurden entfernt',
    logLevel: 'WARN',
  },
  GET_BENUTZERDATEN_FAILED: {
    code: 'DPLC-0012',
    message: 'Benutzerdaten-Abruf nicht möglich',
    logLevel: 'WARN',
  },
  GET_MITTEILUNGEN_FAILED: {
    code: 'DPLC-0013',
    message: 'Mitteilungen-Abruf nicht möglich',
    logLevel: 'WARN',
  },
};

const MESSAGES_LEVEL_WARN = [/Network Error/, /Request aborted/];

const getIncidentsApi = () => {
  const Config = ConfigProvider.get();
  const configuration = new Configuration();
  configuration.basePath = Config.clientLoggingServiceEndpunkt;
  return new IncidentsApi(configuration);
};

const MAX_STRING_LENGTH = 2000;

const truncateWithEllipsis = (str: string) => {
  return str.length > MAX_STRING_LENGTH ? str.slice(0, MAX_STRING_LENGTH - 5) + '(...)' : str;
};

const deviceToGeraet: Record<string, Geraet> = {
  mobile: 'phone',
  tablet: 'tablet',
};

export const postError = (
  errorType: IncidentType,
  clientApiKey: string,
  error?: unknown,
  correlationId?: CorrelationId,
  userJWT?: string,
) => {
  let message: string | undefined = undefined;
  let trace: string | undefined = undefined;

  if (error instanceof Error) {
    message = error.message;
    trace = error.stack;
  } else if (error instanceof ErrorEvent) {
    message = error.message;
    trace = error.error instanceof Error ? error.error.stack : JSON.stringify(error.error);
  } else {
    trace = JSON.stringify(error);
  }

  if (isAxiosError(error) && error.response?.data) {
    trace = `Response: ${JSON.stringify(error.response.data)}\n${trace}`;
  }
  if (isAxiosError(error) && error.config) {
    trace = `Request url: ${error.config?.url}\n${trace}`;
  }

  postIncident(errorType, clientApiKey, message, trace, correlationId, userJWT);
};

const postIncident = (
  errorType: IncidentType,
  clientApiKey: string,
  errorMessage?: string,
  trace?: string,
  correlationId?: CorrelationId,
  userJWT?: string,
) => {
  const incidentLog = possibleLogs[errorType];
  const message = errorMessage ? incidentLog.message + ': ' + errorMessage : incidentLog.message;
  const diagnosticId = correlationId ? correlationId.requestId : uuidv4();
  const logLevel = MESSAGES_LEVEL_WARN.some((regex) => regex.test(message)) ? LogLevel.Warn : incidentLog.logLevel;

  const uaParser = new UAParser(navigator.userAgent);
  const { browser, os, device } = uaParser.getResult();
  const userAgent: UserAgent | undefined = navigator
    ? {
        userAgentString: navigator.userAgent,
        browser: {
          name: browser.name,
          version: browser.version,
        },
        os: {
          name: os.name,
          version: os.version,
        },
      }
    : undefined;

  const incidentError: IncidentError = {
    code: incidentLog.code,
    message: truncateWithEllipsis(message),
    logLevel: logLevel ?? LogLevel.Error,
    trace: trace && truncateWithEllipsis(trace),
    url: truncateWithEllipsis(window.location.href),
    createdAt: new Date().toISOString(),
  };
  const incident: Incident = {
    diagnosticId: diagnosticId,
    errors: [incidentError],
    userAgent,
    device: device.type ? deviceToGeraet[device.type] : undefined,
    resolution: window.innerWidth && window.innerHeight ? `${window.innerWidth}x${window.innerHeight}` : undefined,
    touch: 'ontouchstart' in window,
  };
  const incidentsApi = getIncidentsApi();
  const jwtValid = userJWT ? TokenEnsurer.validLongEnough(new BaJwt(userJWT)) : false;
  incidentsApi
    .postIncident(incident, createHeaders(clientApiKey, diagnosticId, correlationId, jwtValid ? userJWT : undefined))
    .catch((error) => console.error(error));
};
